import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ChatService } from 'src/core/services/chat.service';
import { DataService } from 'src/core/services/data.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { Table, TableModule } from 'primeng/table';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { WidgetService } from 'src/core/services/widget.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { catchError, defer, distinctUntilChanged, EMPTY, filter, finalize, firstValueFrom, forkJoin, iif, map, merge, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { PSMData } from 'src/core/types/systemhealth.types';
import { upcomingserviceData, upcomingserviceSiteData } from 'src/core/types/upcomingservicedetails';
import { ApiResponse } from 'src/core/types/general.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SortEvent } from 'primeng/api';
import { SelectedSystem } from 'src/core/types/selectedSystem.types';
import { NotiDetails } from 'src/core/types/servicehistory.types';
import { ModalService } from 'src/core/services/modal.service';
import ExcelJS from 'exceljs';
import { SubscriptionService } from 'src/core/services/subscription.service';

@Component({
  selector: 'app-upcoming-services-details',
  imports: [NgShuiModule, FormsModule, CommonModule, TableModule, TranslocoModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './upcoming-services-details.component.html',
  styleUrl: './upcoming-services-details.component.css'
})
export class UpcomingServicesDetailsComponent {
  @ViewChild('table') table: Table;
  selectedWidget: selectedWidget = 'None';
  screenInfo$ = this.deviceService.screenInfo$;
  isError: boolean;
  isLoading: boolean
  selectedRange: number;
  displayedTabs: any[] = [];
  upcomingservicedetails: any;
  @Input() tabs: any[] = [];
  //activeTab: string = "Planned Site Visit";
  //switchTab = this.widgetService.switchTab.bind(this.widgetService);
  workareaSize: workareaSize = "small";
  onMaximize: () => void;
  onMinimize: () => void;
  private destroyRef = inject(DestroyRef);
  isDevice: boolean;
  serial: string;
  onExpand: (event?: any) => void;
  isDownloading: boolean = false;
  downloadError: string | null = null;

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    public selectedContentService: SelectedContentService,
    private chatService: ChatService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private modalService: ModalService,
    private subscriptionService: SubscriptionService,
    private translocoService: TranslocoService
  ) {
    this.onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
    this.onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
    this.onExpand = () => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }
}

  private translate(key: string, params?: Record<string, string | number>): string {
    return this.translocoService.translate(key, params);
  }

  private getExportHeaders(): string[] {
    return [
      this.translate('upcomingServicesDetails.table.serialNumber'),
      this.translate('upcomingServicesDetails.table.type'),
      this.translate('upcomingServicesDetails.table.id'),
      this.translate('upcomingServicesDetails.table.description'),
      this.translate('upcomingServicesDetails.table.opened'),
      this.translate('upcomingServicesDetails.table.scheduled'),
      this.translate('upcomingServicesDetails.table.originalNotificationText'),
      this.translate('upcomingServicesDetails.table.aiSummary')
    ];
  }

  private createDownloadId(): string {
    try {
      const anyCrypto: any = (globalThis as any)?.crypto;
      if (anyCrypto?.randomUUID) return anyCrypto.randomUUID();
    } catch { }

    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  private pushDownloadSnack(
    id: string,
    type: 'warning' | 'success' | 'error',
    title: string,
    message: string
  ) {
    const entry = {
      id,
      type,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false
    };

    const upsertById = (list: any[]) => {
      const existingIndex = (list ?? []).findIndex((n: any) => n?.id === id);
      if (existingIndex >= 0) {
        const next = [...list];
        next[existingIndex] = { ...next[existingIndex], ...entry };
        return next;
      }
      return [...(list ?? []), entry];
    };

    const current = this.subscriptionService.$notifications.getValue();
    const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

    this.subscriptionService.$notifications.next(upsertById(current));
    this.subscriptionService.$notificationsCenter.next(upsertById(currentCenter));
  }

  private reopenNotificationsCenter() {
    this.subscriptionService.$closeNotifications.next();
    setTimeout(() => {
      this.subscriptionService.$openNotifications.next();
    }, 600);
  }

  private failDownloadPreparation(snackId: string, message: string): void {
    this.isDownloading = false;
    this.downloadError = message;
    this.pushDownloadSnack(
      snackId,
      'error',
      this.translate('upcomingServicesDetails.download.errorTitle'),
      this.translate('upcomingServicesDetails.download.tryAgainLater')
    );
    this.reopenNotificationsCenter();
  }

  private finalizeDownload(snackId: string, filePrepared: boolean, hadRowError: boolean) {
    if (!filePrepared) {
      this.pushDownloadSnack(
        snackId,
        'error',
        this.translate('upcomingServicesDetails.download.errorTitle'),
        this.translate('upcomingServicesDetails.download.tryAgainLater')
      );
    } else {
      this.pushDownloadSnack(
        snackId,
        'success',
        this.translate('upcomingServicesDetails.download.successTitle'),
        hadRowError
          ? this.translate('upcomingServicesDetails.download.successMessagePartial')
          : this.translate('upcomingServicesDetails.download.successMessageComplete')
      );
    }

    this.reopenNotificationsCenter();
  }


  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory' | 'funcloc' | 'upcomingServices') => {
    this.daterangeService.changeRange(range, type);
  };



  ngOnInit(): void {
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);
      }
    });
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });
    this.loadingService.$isUpcomingServiceDetailsLoading.subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loadingService.$isUpcomingServiceDetailsError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tab is currently active
    // this.widgetService.$activeTab.subscribe((tab) => {
    //   if (tab) {
    //     this.activeTab = tab;
    //   }
    //   else {
    //     this.activeTab = this.displayedTabs[0]?.name;
    //   }
    // });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeUpcomingServices;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshUpcomingServices.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );


    triggers$.pipe(
      tap(() => {
        this.loadingService.$isUpcomingServiceDetailsLoading.next(true);
        this.loadingService.$isUpcomingServiceDetailsError.next(false);
        this.upcomingservicedetails = null;
      }),
      switchMap(({ rangeValue, force }) => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.upcomingPullDate;
        const pulledFor = this.selectedSystemService.upcomingPulledFor;

        this.isDevice = this.selectedContentService.selection.kind !== "site";
        this.serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
        const funcloc = this.selectedContentService.currentSite?.Id;

        const cacheKey = this.isDevice ? (this.serial + material) : funcloc;
        const shouldFetch = force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== cacheKey;

        const apiCall$ = this.isDevice
          ? this.dataService.getUpcomingServicesDetails(material, this.serial)
          : this.dataService.getUpcomingServicesSiteDetails(funcloc);

        return iif(
          () => shouldFetch,
          apiCall$.pipe(
            tap(({ response, status }: ApiResponse<upcomingserviceData | upcomingserviceSiteData>) => {
              console.log(force ? 'Forced refresh of upcoming services data' : 'Fetching new upcoming services data');
              this.selectedSystemService.upcomingPullDate = now;
              this.selectedSystemService.upcomingPulledFor = cacheKey;
              if (status === 200) {
                this.selectedSystemService.$upcomingservicedetails.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$upcomingservicedetails.next(null);
                this.loadingService.$isUpcomingServiceDetailsEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$upcomingservicedetails.pipe(
            take(1),
            tap(() => console.log('Using cached upcoming services data')),
            map(cached => ({ response: cached, status: 200 }))
          )
        ).pipe(
          map(({ response }) => ({ response, rangeValue, isDevice: this.isDevice }))
        );
      }),
      map(({ response, rangeValue, isDevice }) => {
        const aggregatedServices = isDevice
          ? response?.details?.items || []
          : (response?.details?.devices || []).flatMap(device =>
            (device.services || [])
          );

        return {
          services: aggregatedServices.filter(s => s.service_type !== 'UV'),
          rangeValue
        };
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ services, rangeValue }) => {
        this.selectedRange = rangeValue;
        this.upcomingservicedetails = services;
        this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
      },
      error: (error) => {
        console.error('Error fetching upcoming services data:', error);
        this.upcomingservicedetails = [];
        this.loadingService.$isUpcomingServiceDetailsError.next(true);
        this.loadingService.$isUpcomingServiceDetailsLoading.next(false);
      }
    });


    //When Notification is selected, we need to fetch details + summary if not already cached
    this.selectedSystemService.$selectedNotification.pipe(
      // ignore falsy & repeated selections
      filter((id): id is string => !!id),
      distinctUntilChanged(),

      // pull in current cache and the selected system (for material/serial)
      withLatestFrom(
        this.selectedSystemService.$notificationDetails,   // NotiDetails[]
        this.selectedSystemService.$notificationSummary,   // Summary[] (assumed existing)
        this.selectedSystemService.$upcomingservicedetails
      ),

      // log if cached
      tap(([id, details, summary]) => {
        if ((details ?? []).some(d => d.id === id)) {
          console.log('Using cached notification details for', id);
        }
        if ((summary ?? []).some((s: any) => s?.id === id)) {
          console.log('Using cached notification summary for', id);
        }
      }),

      // fetch missing resources (details, summary) in parallel
      switchMap(([id, detailsCache, summaryCache, upcomingservice]) => {
        const hasDetails = (detailsCache ?? []).some(d => d.id === id);
        const hasSummary = (summaryCache ?? []).some((s: any) => s?.id === id);

        // Set loading states only for what will be fetched
        if (!hasDetails) {
          this.loadingService.$isNotificationDetailsLoading.next(true);
          this.loadingService.$isNotificationDetailsError.next(false);
        }
        if (!hasSummary) {
          this.loadingService.$isNotificationSummaryLoading.next(true);
          this.loadingService.$isNotificationSummaryError.next(false);
        }

        let system;

        if (this.isDevice) {
          system = upcomingservice;
        }
        else {
          system = upcomingservice.details.devices.find((device: any) =>
            device.services.some((service: any) => service.upcoming_service_id === id)
          );
        }

        const material = system?.material_number
        const serial = system?.serial_number;

        const details$ = iif(
          () => !hasDetails,
          this.dataService.getNotificationDetails(material, serial, id).pipe(
            tap(({ response }: ApiResponse<NotiDetails>) => {
              const current = this.selectedSystemService.$notificationDetails.value ?? [];
              if (!current.some(d => d.id === id)) {
                this.selectedSystemService.$notificationDetails.next([...current, response]);
              }
            }),
            map(() => ({ ok: true })),
            catchError(err => {
              console.error('Failed to fetch notification details', err);
              this.loadingService.$isNotificationDetailsError.next(true);
              // Emit a sentinel so forkJoin waits for both and doesn't cancel the other
              return of({ ok: false, error: err });
            }),
            finalize(() => {
              this.loadingService.$isNotificationDetailsLoading.next(false);
            })
          ),
          // already cached
          defer(() => [{ ok: true }])
        );

        const summary$ = iif(
          () => !hasSummary,
          this.dataService.getNotificationSummary(id).pipe(
            tap(({ response }) => {
              const current = this.selectedSystemService.$notificationSummary.value ?? [];
              if (!current.some((s: any) => s?.id === id)) {
                this.selectedSystemService.$notificationSummary.next([...current, response]);
              }
            }),
            map(() => ({ ok: true })),
            catchError(err => {
              console.error('Failed to fetch notification summary', err);
              this.loadingService.$isNotificationSummaryError.next(true);
              // Emit a sentinel so forkJoin waits for both and doesn't cancel the other
              return of({ ok: false, error: err });
            }),
            finalize(() => {
              this.loadingService.$isNotificationSummaryLoading.next(false);
            })
          ),
          // already cached
          defer(() => [{ ok: true }])
        );

        // Run both in parallel; each may be a no-op if already cached
        return forkJoin([details$, summary$]).pipe(map(() => id));
      }),

      // auto-unsubscribe on destroy
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (id) => console.log('Fetched/cached details+summary for', id)
    });



    //Handeling once a specific PSM is selected
  

    this.selectedSystemService.$selectedPattern.pipe(
      // ignore falsy & repeated selections
      filter((pattern_id): pattern_id is string => !!pattern_id),
      distinctUntilChanged(),

      // pull in current cache and the selected system (for material/serial)
      withLatestFrom(
        this.selectedSystemService.$proaDetails,
        this.selectedContentService.selectedSystem$.pipe(
          filter((s): s is SelectedSystem => !!s)
        )
      ),

      // log if cached
      tap(([pattern_id, details]) => {
        if ((details ?? []).some(d => d.pattern_id === pattern_id)) {
          console.log('Using cached PSM for', pattern_id);
        }
      }),

      // only proceed if this ID is NOT in the cache yet
      filter(([pattern_id, details]) => !(details ?? []).some(d => d.id === pattern_id)),

      // fetch details for the missing ID
      switchMap(([pattern_id, , system]) => {
        this.loadingService.$isPSMDetailsLoading.next(true);
        this.loadingService.$isPSMDetailsError.next(false);

        return this.dataService
          .getNotificationDetails(system.MaterialNumber, system.SerialNumber, pattern_id)
          .pipe(
            tap((data) => {
              // append safely (guard against races)
              const current = this.selectedSystemService.$proaDetails.value ?? [];
              if (!current.some(d => d.pattern_id === pattern_id)) {
                this.selectedSystemService.$proaDetails.next([...current, data]);
              }
            }),
            map(() => pattern_id),
            catchError(err => {
              console.error('Failed to fetch notification details', err);
              this.loadingService.$isPSMDetailsError.next(true);
              return EMPTY; // swallow error, keep stream alive
            }),
            finalize(() => {
              this.loadingService.$isPSMDetailsLoading.next(false);
            })
          );
      }),

      // auto-unsubscribe on destroy
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (id) => console.log('Added', id, 'to array')
    });
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'serial': // assuming this is a date field
          val1 = a.serial_number.toLowerCase() ?? '';
          val2 = b.serial_number.toLowerCase() ?? '';
          break;
        case 'type': // assuming this is a date field
          val1 = a.service_type.toLowerCase() ?? '';
          val2 = b.service_type.toLowerCase() ?? '';
          break;

        case 'id': // string field
          val1 = a.upcoming_service_id?.toLowerCase() ?? '';
          val2 = b.upcoming_service_id?.toLowerCase() ?? '';
          break;
        case 'description': // string field
          val1 = a.description?.toLowerCase() ?? '';
          val2 = b.description?.toLowerCase() ?? '';
          break;
        case 'opened': // string field
          val1 = new Date(a.opened);
          val2 = new Date(b.opened);
          break;
        case 'scheduled': // string field
          val1 = new Date(a.scheduled);
          val2 = new Date(b.scheduled);
          break;


        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  refresh() {
    this.selectedSystemService.$refreshUpcomingServices.next();
  }


  openWithParams(params: Record<string, string>) {
    // Create a URL object from the current location
    const url = new URL(window.location.href);

    // Remove all existing query params
    url.search = '';

    // Add or update params
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    // Open the new URL in a new browser tab
    window.open(url.toString(), "_blank");
  }

  focusNotification(id, type) {

    if (type === 'PSM') {
      this.selectedSystemService.$selectedPattern.next(id);
      this.modalService.$modalChanged.next({ modal: 'psm', open: true });
    }
    else {
      this.selectedSystemService.$selectedNotification.next(id);
      this.modalService.$modalChanged.next({ modal: 'notification-details', open: true });
    }

  }

  private formatExportDate(value: string | null | undefined): string {
    if (!value || value === '1970-01-01T00:00:00Z') {
      return '-';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(parsedDate);
  }

  private mapExportRow(row: any): (string | number)[] {
    return [
      this.isDevice ? (this.serial || row?.serial_number || '') : (row?.serial_number || ''),
      row?.service_type || '',
      row?.upcoming_service_id || row?.pattern_id || '',
      row?.description || '',
      this.formatExportDate(row?.opened),
      this.formatExportDate(row?.scheduled),
      row?.originalText || '',
      row?.aiSummary || ''
    ];
  }

  private shouldIncludeNotificationContent(row: any): boolean {
    return row?.service_type === 'PM' || row?.service_type === 'MU';
  }

  private async getNotificationExportContentForRow(
    row: any,
    markRowError: () => void
  ): Promise<{ originalText: string; aiSummary: string }> {
    if (!this.shouldIncludeNotificationContent(row)) {
      return { originalText: '', aiSummary: '' };
    }

    const notificationId = row?.upcoming_service_id;
    const material = this.isDevice
      ? this.selectedContentService.currentSystem?.MaterialNumber
      : row?.material_number;
    const serial = this.isDevice
      ? this.selectedContentService.currentSystem?.SerialNumber
      : row?.serial_number;

    if (!notificationId) {
      markRowError();
      return { originalText: '', aiSummary: '' };
    }

    const [details, aiSummary] = await Promise.all([
      this.getNotificationDetailsForExport(material, serial, notificationId, markRowError),
      this.getAiSummaryForExport(notificationId, markRowError)
    ]);

    return {
      originalText: details?.details?.summary || '',
      aiSummary
    };
  }

  private async getNotificationDetailsForExport(
    material: string | undefined,
    serial: string | undefined,
    notificationId: string,
    markRowError: () => void
  ): Promise<NotiDetails | null> {
    if (!material || !serial) {
      markRowError();
      return null;
    }

    try {
      const { response } = await firstValueFrom(this.dataService.getNotificationDetails(material, serial, notificationId));
      return response ?? null;
    } catch (error) {
      console.error('Error fetching original notification text for export:', notificationId, error);
      markRowError();
      return null;
    }
  }

  private async getAiSummaryForExport(notificationId: string, markRowError: () => void): Promise<string> {
    try {
      const { response } = await firstValueFrom(this.dataService.getNotificationSummary(notificationId));
      return response?.summary || '';
    } catch (error) {
      console.error('Error fetching notification summary for export:', notificationId, error);
      markRowError();
      return '';
    }
  }

  private createDownloadFilename(): string {
    const dateStamp = new Date().toISOString().slice(0, 10);
    const identifier = this.isDevice
      ? `${this.selectedContentService.currentSystem?.MaterialNumber || 'material'}_${this.selectedContentService.currentSystem?.SerialNumber || 'serial'}`
      : `${this.selectedContentService.currentSite?.Name || this.selectedContentService.currentSite?.Id || 'site'}`;

    return `upcoming_services_${String(identifier).replace(/[^a-z0-9_-]+/gi, '_')}_${dateStamp}.xlsx`;
  }

  private triggerExcelDownload(buffer: ArrayBuffer | Uint8Array, filename: string) {
    const content = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const arrayBuffer = new ArrayBuffer(content.byteLength);
    new Uint8Array(arrayBuffer).set(content);

    const blob = new Blob([arrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
      link.remove();
    }, 0);
  }

  private async generateAndDownloadWorkbook(headers: string[], rowsAoA: (string | number)[][], filename: string): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Upcoming Services');

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };

    for (const row of rowsAoA) {
      worksheet.addRow(row);
    }

    worksheet.columns = (worksheet.columns ?? []).map((column) => {
      let max = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const value = cell?.value;
        const length = value == null ? 0 : String(value).length;
        if (length > max) {
          max = length;
        }
      });
      column.width = Math.min(Math.max(max + 2, 10), 40);
      return column;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    this.triggerExcelDownload(buffer, filename);
  }

  async download(): Promise<void> {
    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;
    this.downloadError = null;
    const snackId = `download${this.createDownloadId()}`;

    this.pushDownloadSnack(
      snackId,
      'warning',
      this.translate('upcomingServicesDetails.download.preparingTitle'),
      this.translate('upcomingServicesDetails.download.preparingMessage')
    );

    try {
      const currentRows = this.table?.filteredValue ?? this.table?.value ?? this.upcomingservicedetails;
      const rows = Array.isArray(currentRows) ? currentRows : [];

      if (rows.length === 0) {
        this.failDownloadPreparation(snackId, 'No rows to export.');
        return;
      }

      const headers = this.getExportHeaders();
      let hadRowError = false;
      const markRowError = () => {
        hadRowError = true;
      };

      const rowsAoA = await Promise.all(
        rows.map(async (row) => {
          const exportContent = await this.getNotificationExportContentForRow(row, markRowError);
          return this.mapExportRow({
            ...row,
            ...exportContent
          });
        })
      );
      await this.generateAndDownloadWorkbook(headers, rowsAoA, this.createDownloadFilename());
      this.finalizeDownload(snackId, true, hadRowError);
    } catch (error) {
      console.error('Error exporting upcoming services table:', error);
      this.downloadError = error instanceof Error ? error.message : 'Failed to generate Excel file.';
      this.finalizeDownload(snackId, false, false);
    } finally {
      this.isDownloading = false;
    }
  }

}
